import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle } from "@material-ui/core";
import { clone, get } from "lodash";
import Button from "../../../components/CustomButtons/Button";
import GridContainer from "../../../components/Grid/GridContainer";
import GridItem from "../../../components/Grid/GridItem";
import Table from "../../../components/Table/Table";
import NotificationAsk from "../../../components/Notification/NotificationAsk";
import axios from "store/axios/axios-commission-splits";
import { addCommissionSplits } from "../RSheetsLogic";

const TYPE_TO_RS_TYPE = {
    1: 'residential',
    2: 'lease',
    3: 'sublease',
    4: 'sale',
    5: 'tenant rep',
    6: 'consulting',
    7: 'commission',
    8: 'no listing',
    9: 'lease assignment',
};

const safeParse = (s) => {
    try { return JSON.parse(s); } catch (e) { return {}; }
};

const getJsonData = (cs) => {
    if (!cs) return {};
    if (typeof cs.json_data === 'string') return safeParse(cs.json_data);
    return cs.json_data || {};
};

const collectRsAssociateIds = (data) => {
    const ids = new Set();
    [data.commission_splits_agent, data.commission_splits_agent2, data.commission_splits_agent3].forEach((list) => {
        if (Array.isArray(list)) {
            list.forEach((c) => { if (c && c.id != null) ids.add(String(c.id)); });
        }
    });
    return ids;
};

const collectCsAssociateIds = (cs) => {
    const ids = new Set();
    const contacts = get(getJsonData(cs), 'contacts', []);
    if (Array.isArray(contacts)) {
        contacts.forEach((c) => { if (c && c.contact_id != null) ids.add(String(c.contact_id)); });
    }
    return ids;
};

const sameSet = (a, b) => {
    if (a.size !== b.size) return false;
    for (const v of a) if (!b.has(v)) return false;
    return true;
};

const associateNames = (cs) => {
    const contacts = get(getJsonData(cs), 'contacts', []);
    if (!Array.isArray(contacts)) return '';
    return contacts
        .map((c) => `${c.first_name || ''} ${c.last_name || ''}`.trim())
        .filter(Boolean)
        .join(', ');
};

const projectName = (cs) => {
    const j = getJsonData(cs);
    return j.project_name || j.property_name || '';
};

const AttachedCommissionSplits = (props) => {
    const { data, setData } = props;
    const [loading, setLoading] = useState(false);
    const [currentCs, setCurrentCs] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerList, setPickerList] = useState([]);
    const [pickerLoading, setPickerLoading] = useState(false);
    const [viewExpanded, setViewExpanded] = useState(false);
    const [confirmSwap, setConfirmSwap] = useState(null);
    const [pickerSearch, setPickerSearch] = useState('');

    const rsAssociateIds = collectRsAssociateIds(data);

    const loadCurrent = async () => {
        setLoading(true);
        try {
            const csId = data.cs_id;
            if (csId) {
                const r = await axios.get('/entry?id=' + csId);
                setCurrentCs(r.data || null);
            } else if (data.id) {
                const r = await axios.get('/entries?rs_id=' + data.id);
                const list = Array.isArray(r.data) ? r.data : [];
                setCurrentCs(list.find((cs) => cs.status === 13) || list[0] || null);
            } else {
                setCurrentCs(null);
            }
        } catch (e) {
            console.log('AttachedCommissionSplits load error', e);
            setCurrentCs(null);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadCurrent();
    }, [data.cs_id, data.id]);

    const applySwap = async (cs) => {
        const newData = clone(data);
        const csForCopy = clone(cs);
        if (typeof csForCopy.json_data === 'string') {
            csForCopy.json_data = safeParse(csForCopy.json_data);
        }
        const rsType = TYPE_TO_RS_TYPE[data.type] || 'lease';
        await addCommissionSplits(newData, rsType, csForCopy);
        newData.cs_id = cs.id;
        setData(newData);
        setCurrentCs(cs);
        setPickerOpen(false);
        setConfirmSwap(null);
        setViewExpanded(false);
    };

    const openPicker = async () => {
        setPickerOpen(true);
        setPickerLoading(true);
        setPickerSearch('');
        try {
            const r = await axios.get('/entries?status=13');
            setPickerList(Array.isArray(r.data) ? r.data : []);
        } catch (e) {
            console.log('picker fetch error', e);
            setPickerList([]);
        }
        setPickerLoading(false);
    };

    const filteredPickerList = (() => {
        const q = pickerSearch.trim().toLowerCase();
        if (!q) return pickerList;
        return pickerList.filter((cs) => {
            const idStr = String(cs.id || '');
            const project = (projectName(cs) || '').toLowerCase();
            const names = (associateNames(cs) || '').toLowerCase();
            return idStr.indexOf(q) !== -1 || project.indexOf(q) !== -1 || names.indexOf(q) !== -1;
        });
    })();

    const pickCs = (cs) => {
        if (!sameSet(rsAssociateIds, collectCsAssociateIds(cs))) {
            setConfirmSwap(cs);
            return;
        }
        applySwap(cs);
    };

    const currentRow = currentCs
        ? [[
            projectName(currentCs) || '-',
            associateNames(currentCs) || '-',
            (
                <span>
                    <Button color="primary" size="sm" style={{ marginRight: 8 }} onClick={() => setViewExpanded(!viewExpanded)}>
                        {viewExpanded ? 'Hide' : 'View'}
                    </Button>
                    <Button color="white" size="sm" onClick={openPicker}>Swap</Button>
                </span>
            )
        ]]
        : [];

    const csContacts = currentCs ? (get(getJsonData(currentCs), 'contacts', []) || []) : [];

    return (
        <div className="boxed mt-20" style={{ width: '100%' }}>
            <GridContainer>
                <GridItem xs={12} sm={12} md={12}>
                    <h3 style={{ textTransform: 'uppercase', fontWeight: 'bold' }}>Attached Commission Splits</h3>
                </GridItem>

                {loading && (
                    <GridItem xs={12} sm={12} md={12}>
                        <em>Loading...</em>
                    </GridItem>
                )}

                {!loading && currentCs && (
                    <GridItem xs={12} sm={12} md={12}>
                        <Table
                            tableHeaderColor="primary"
                            tableHead={["Project", "Associates", ""]}
                            tableData={currentRow}
                        />
                    </GridItem>
                )}

                {!loading && currentCs && viewExpanded && (
                    <GridItem xs={12} sm={12} md={12}>
                        <div className="boxed">
                            <h4>Associates</h4>
                            <Table
                                tableHeaderColor="primary"
                                tableHead={["Name", "Commission %"]}
                                tableData={csContacts.map((c) => [
                                    `${c.first_name || ''} ${c.last_name || ''}`.trim(),
                                    c.commission_percent != null && c.commission_percent !== '' ? `${c.commission_percent} %` : '-'
                                ])}
                            />
                        </div>
                    </GridItem>
                )}

                {!loading && !currentCs && (
                    <>
                        <GridItem xs={12} sm={12} md={12}>
                            <em>No commission splits form attached</em>
                        </GridItem>
                        <GridItem container justify="flex-end" xs={12} sm={12} md={12}>
                            <Button color="primary" onClick={openPicker}>
                                <i className="fa fa-link" style={{ marginRight: 6 }}></i> ATTACH COMMISSION SPLIT FORM
                            </Button>
                        </GridItem>
                    </>
                )}
            </GridContainer>

            {pickerOpen && (
                <Dialog open={true} onClose={() => setPickerOpen(false)} fullWidth maxWidth="md">
                    <DialogTitle>
                        <div style={{ textAlign: 'center', fontWeight: 'bold' }}>Pick an Approved Commission Split Form</div>
                    </DialogTitle>
                    <div style={{ padding: 20 }}>
                        {!pickerLoading && pickerList.length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                                <input
                                    type="text"
                                    placeholder="Search by project or associate name..."
                                    value={pickerSearch}
                                    onChange={(e) => setPickerSearch(e.target.value)}
                                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 }}
                                    autoFocus
                                />
                            </div>
                        )}
                        {pickerLoading && <em>Loading...</em>}
                        {!pickerLoading && pickerList.length === 0 && (
                            <em>No approved (Completed) commission splits forms available.</em>
                        )}
                        {!pickerLoading && pickerList.length > 0 && filteredPickerList.length === 0 && (
                            <em>No matches.</em>
                        )}
                        {!pickerLoading && filteredPickerList.length > 0 && (
                            <Table
                                tableHeaderColor="primary"
                                tableHead={["Project", "Associates", ""]}
                                tableData={filteredPickerList.map((cs) => [
                                    projectName(cs) || '-',
                                    associateNames(cs) || '-',
                                    <Button color="primary" size="sm" onClick={() => pickCs(cs)}>Select</Button>
                                ])}
                            />
                        )}
                        <div style={{ marginTop: 16, textAlign: 'right' }}>
                            <Button color="white" onClick={() => setPickerOpen(false)}>Cancel</Button>
                        </div>
                    </div>
                </Dialog>
            )}

            {confirmSwap && (
                <NotificationAsk
                    open={true}
                    close={() => setConfirmSwap(null)}
                    title="Different Associates"
                    message="Warning: the selected commission split form has different associates than this routing sheet. Swapping will replace the current associates and commission percentages on this Routing Sheet"
                    success_button_text="Confirm Swap"
                    success={() => applySwap(confirmSwap)}
                />
            )}
        </div>
    );
};

export default AttachedCommissionSplits;
